import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Plus, Upload, Download, X, Copy, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import DataTable from "./DataTable";
import UserForm from "./UserForm";
import { BASE_URL } from "shared/lib/config";
import BulkImportSummaryModal from "./BulkImportSummaryModal";
import BulkImportModal from "./BulkImport";
import DeleteConfirmModal from "./DeleteConfirmModal";

/* =========================
 * Voucher Modal
 * ========================= */
function VouchersModal({ open, onClose, userName, vouchers }) {
  if (!open) return null;

  const list = Array.isArray(vouchers) ? vouchers.filter(Boolean) : [];

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Tersalin ke clipboard");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const copyAll = () => {
    if (!list.length) return;
    copyText(list.join(", "));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Daftar Voucher</h3>
            <p className="text-sm text-gray-500">
              {userName ? `Pengguna: ${userName}` : "Voucher pengguna"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold">{list.length}</span>
            </p>

            <button
              type="button"
              onClick={copyAll}
              disabled={!list.length}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                list.length
                  ? "bg-gray-800 text-white hover:bg-gray-900"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              title={list.length ? "Salin semua voucher" : "Tidak ada voucher"}
            >
              <Copy className="w-4 h-4" />
              Copy all
            </button>
          </div>

          {list.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
              Pengguna ini belum memiliki voucher.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {list.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => copyText(v)}
                  className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  title="Klik untuk menyalin voucher"
                >
                  <span className="font-mono">{v}</span>
                  <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersManager() {
  const [deleteModal, setDeleteModal] = useState({
  open: false,
  title: "Konfirmasi",
  message: "",
  tone: "danger",
  confirmText: "Ya, Hapus",
  cancelText: "Batal",
  resolve: null,
});
  const confirmDelete = useCallback(
  ({
    title = "Konfirmasi",
    message = "",
    tone = "danger",
    confirmText = "Ya, Hapus",
    cancelText = "Batal",
  } = {}) =>
    new Promise((resolve) => {
      setDeleteModal({
        open: true,
        title,
        message,
        tone,
        confirmText,
        cancelText,
        resolve,
      });
    }),
  []
);

  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [allSchools, setAllSchools] = useState([]);

  const [loadError, setLoadError] = useState("");
  const [me, setMe] = useState(null);

  // Server pagination truth
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Controlled by DataTable UI
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [filterRole, setFilterRole] = useState("");
  const [filterSchool, setFilterSchool] = useState("");

  // Server-side search
  const [serverSearch, setServerSearch] = useState("");

  // Loading UX
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const abortRef = useRef(null);
  const firstLoadRef = useRef(true);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Import
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Voucher modal
  const [showVouchersModal, setShowVouchersModal] = useState(false);
  const [voucherModalUser, setVoucherModalUser] = useState(null);

  const dataTableRef = useRef(null);

  // Autocomplete
  const [schoolFocused, setSchoolFocused] = useState(false);
  const schoolInputRef = useRef(null);

  const schoolQuery = useMemo(
    () => filterSchool.trim().toLowerCase(),
    [filterSchool]
  );

  const suggestedSchools = useMemo(() => {
    if (!schoolQuery) return [];
    return schools
      .filter((s) => (s?.name || "").toLowerCase().includes(schoolQuery))
      .slice(0, 8);
  }, [schools, schoolQuery]);

  const showSchoolSuggestions =
    schoolFocused && schoolQuery.length > 0 && suggestedSchools.length > 0;

  const selectSchool = useCallback((name) => {
    setFilterSchool(name);
    setSchoolFocused(false);
    requestAnimationFrame(() => {
      schoolInputRef.current?.focus();
    });
  }, []);

  const getStore = () => {
    if (typeof window === "undefined") return localStorage;
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage;
  };
  const decodeJwtPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isSelfUser = useCallback(
  (u) => {
    if (!me) return false;
    const emailMatch =
      me.email && u?.email && String(me.email).toLowerCase() === String(u.email).toLowerCase();
    const uidMatch =
      me.firebase_uid && u?.firebase_uid && String(me.firebase_uid) === String(u.firebase_uid);
    return Boolean(emailMatch || uidMatch);
  },
  [me]
);

const loadMeFromToken = () => {
  const store = getStore();
  const token = store.getItem("idToken");
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  // Firebase ID token commonly has these fields:
  const email = payload.email || null;
  const uid = payload.user_id || payload.sub || null;

  return { email, firebase_uid: uid };
};

  useEffect(() => {
    const mine = loadMeFromToken();
    setMe(mine);
  }, []);

  useEffect(() => {
  const loadAllSchools = async () => {
    try {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) return;

      const res = await fetch(`${BASE_URL}/api/admin/schools`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) return;

      const json = await res.json();

      const list = Array.isArray(json) ? json : json.data || [];

      setAllSchools(
        list
          .map((s) => ({ id: s.id, name: s.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch {
      // ignore
    }
  };
  
  loadAllSchools();

}, []);


  const fetchUsers = useCallback(
    async ({ reason = "load", targetPage = page } = {}) => {
      try {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        if (reason === "search") setSearching(true);
        else setLoading(true);

        setLoadError("");

        const store = getStore();
        const token = store.getItem("idToken");
        if (!token) throw new Error("Tidak ada token login. Silakan login kembali.");

        const params = new URLSearchParams();
        params.set("page", String(targetPage));
        const limitToSend = pageSize === "all" ? "2000" : String(pageSize); 
        params.set("limit", limitToSend);
        if (filterRole) params.set("role", filterRole);
        if (serverSearch.trim()) params.set("search", serverSearch.trim());
        if (filterSchool.trim()) params.set("schoolName", filterSchool.trim());
        const res = await fetch(`${BASE_URL}/api/admin/users?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.message || "Gagal mengambil data pengguna");
        }

        const json = await res.json();

        const mappedUsers =
          json.data?.map((u) => ({
            id: u.id,
            firebase_uid: u.firebaseUid,
            name: u.name,
            email: u.email,
            role: u.role,
            school_id: u.school?.id || null,
            class_id: u.classId || null,
            created_at: u.createdAt,
            updated_at: u.updatedAt,
            initial_password: u.initialPassword || null,
            vouchers: Array.isArray(u.vouchers) ? u.vouchers : [],
            _school: u.school || null,
          })) || [];

        setUsers(mappedUsers);

        if (json.pagination) setPagination(json.pagination);
        else setPagination((p) => ({ ...p, page: targetPage, limit: pageSize }));

        const schoolMap = new Map();
        for (const u of json.data || []) {
          if (u.school?.id) {
            schoolMap.set(u.school.id, { id: u.school.id, name: u.school.name });
          }
        }
        setSchools(Array.from(schoolMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("[UsersManager] fetchUsers error:", err);
        const msg = err.message || "Gagal memuat data pengguna";
        setLoadError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
        setSearching(false);
      }
    },
    [page, pageSize, filterRole, serverSearch, filterSchool]
  );

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      fetchUsers({ reason: "load", targetPage: page });
      return;
    }
    fetchUsers({ reason: "page", targetPage: page });
  }, [page, fetchUsers]);

  const prevRef = useRef({ pageSize, filterRole, serverSearch, filterSchool });

  useEffect(() => {
    const prev = prevRef.current;
    const changed =
      prev.pageSize !== pageSize ||
      prev.filterRole !== filterRole ||
      prev.serverSearch !== serverSearch ||
      prev.filterSchool !== filterSchool;
    prevRef.current = { pageSize, filterRole, serverSearch, filterSchool };


    if (!changed) return;

    if (page === 1) fetchUsers({ reason: "search", targetPage: 1 });
    else setPage(1);
  }, [pageSize, filterRole, serverSearch, filterSchool, page, fetchUsers]);

  const handleUserSaved = async () => {
    await fetchUsers({ reason: "load", targetPage: page });
  };

  const handleDelete = async (user) => {
    if (isSelfUser(user)) {
  toast.error("Kamu tidak bisa menghapus akunmu sendiri.");
  return;
}

    const ok = await confirmDelete({
  message: `Hapus pengguna "${user.name}"?\nTindakan ini tidak dapat dibatalkan.`,
});

    if (!ok) return;

    const toastId = toast.loading("Menghapus pengguna...");

    try {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) {
        toast.update(toastId, {
          render: "Sesi login sudah habis. Silakan login ulang.",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const res = await fetch(`${BASE_URL}/api/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawText = await res.text();
      let body = {};
      try {
        body = rawText ? JSON.parse(rawText) : {};
      } catch {
        // ignore
      }

      if (!res.ok) {
        toast.update(toastId, {
          render: body.message || `Gagal menghapus pengguna. (status ${res.status})`,
          type: "error",
          isLoading: false,
          autoClose: 3500,
        });
        return;
      }

      toast.update(toastId, {
        render: body.message || "Pengguna berhasil dihapus.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });

      await fetchUsers({ reason: "load", targetPage: page });
    } catch (err) {
      console.error("[UsersManager] delete user error:", err);
      toast.update(toastId, {
        render: "Terjadi kesalahan saat menghapus pengguna.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const handleBulkDelete = async (selectedUsers) => {
    if (!selectedUsers?.length) return;
    const safeUsers = (selectedUsers || []).filter((u) => !isSelfUser(u));
const blockedCount = (selectedUsers?.length || 0) - safeUsers.length;

if (blockedCount > 0) {
  toast.info("Akun kamu sendiri tidak bisa dihapus dan akan dilewati.");
}

if (!safeUsers.length) return;

   const ok = await confirmDelete({
  message: `Hapus ${safeUsers.length} pengguna?\nTindakan ini tidak dapat dibatalkan.`,
});


    if (!ok) return;

    const toastId = toast.loading("Menghapus pengguna terpilih...");

    try {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) {
        toast.update(toastId, {
          render: "Sesi login sudah habis. Silakan login ulang.",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const failed = [];
      for (const u of safeUsers) {
        try {
          const res = await fetch(`${BASE_URL}/api/admin/users/${u.id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) failed.push(u);
        } catch {
          failed.push(u);
        }
      }

      await fetchUsers({ reason: "load", targetPage: page });

      toast.update(toastId, {
        render: !failed.length
          ? "Semua pengguna terpilih berhasil dihapus."
          : `Sebagian pengguna gagal dihapus (${failed.length}).`,
        type: !failed.length ? "success" : "error",
        isLoading: false,
        autoClose: !failed.length ? 2500 : 4000,
      });
    } catch (err) {
      console.error("[UsersManager] bulk delete error:", err);
      toast.update(toastId, {
        render: "Terjadi kesalahan saat menghapus beberapa pengguna.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleFormClose = (updatedUser) => {
    setShowForm(false);
    setEditingUser(null);

    if (updatedUser?.id) {
      setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
    }
  };

  const getSchoolName = (schoolId) => {
    if (!schoolId) return "-";
    return schools.find((s) => s.id === schoolId)?.name || "-";
  };

  const getRoleLabelLocal = (role) => {
    const roles = {
      admin: "Admin",
      teacher: "Guru",
      student: "Siswa",
      principal: "Kepala Sekolah",
      generalUser: "Pengguna Umum",
      financeAdmin: "Finance",
    };
    return roles[role] || role;
  };

  const openVoucherModal = (user) => {
    setVoucherModalUser(user);
    setShowVouchersModal(true);
  };

  const copyVoucher = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Voucher tersalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  };
  const copyInitialPassword = async (pw) => {
  if (!pw) return;
  try {
    await navigator.clipboard.writeText(pw);
    toast.success("Password awal tersalin");
  } catch {
    toast.error("Gagal menyalin");
  }
};

  /* ---------- table columns ---------- */
  const columns = [
    {
      key: "name",
      label: "Nama",
      className: "w-48 text-left",
      render: (user) => <span className="font-medium">{user.name}</span>,
    },
    {
      key: "email",
      label: "Email",
      className: "w-60 text-left",
      render: (user) => <span>{user.email}</span>,
    },
    {
      key: "role",
      label: "Role",
      className: "w-32 text-center",
      render: (user) => (
        <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-[11px] font-semibold whitespace-nowrap">
          {getRoleLabelLocal(user.role)}
        </span>
      ),
    },
  {
  key: "initial_password",
  label: "Password Awal",
  className: "w-70 text-center",
  render: (user) => {
    const pw = user.initial_password;

    if (!pw) return <span className="text-xs text-gray-400">N/A</span>;

    return (
      <button
        type="button"
        onClick={() => copyInitialPassword(pw)}
        className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        title="Klik untuk menyalin password awal"
      >
        <span className="font-mono">{pw}</span>
        <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700" />
      </button>
    );
  },
},


    // ✅ Voucher column (prettier behavior)
  // ✅ Voucher column (STACK pill + single pill copy)
{
  key: "vouchers",
  label: "Voucher",
  className: "w-64 text-left",
  render: (user) => {
    const list = Array.isArray(user.vouchers) ? user.vouchers.filter(Boolean) : [];
    const count = list.length;

    if (count === 0) return <span className="text-xs text-gray-400">-</span>;

    // 1 voucher -> pill click => copy
    if (count === 1) {
      const v = list[0];
      return (
        <button
          type="button"
          onClick={() => copyVoucher(v)}
          className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          title="Klik untuk menyalin voucher"
        >
          <span className="font-mono">{v}</span>
          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700" />
        </button>
      );
    }

    // >1 voucher -> stacked pill click => open modal
    const first = list[0];

    return (
      <button
        type="button"
        onClick={() => openVoucherModal(user)}
        className="relative inline-flex items-center"
        title="Klik untuk melihat semua voucher"
      >
        {/* stack layers behind (same size as main pill, no fixed width) */}
        <span className="absolute inset-0 translate-x-[-4px] translate-y-[3px] rounded-full border border-gray-200 bg-white opacity-60" />
        <span className="absolute inset-0 translate-x-[-2px] translate-y-[1.5px] rounded-full border border-gray-200 bg-white opacity-80" />

        {/* main pill */}
        <span className="relative inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
          <span className="font-mono">{first}</span>

          {/* count badge */}
          <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-2 py-[1px] text-[10px] font-semibold text-blue-700 border border-blue-100">
            +{count - 1}
          </span>
        </span>
      </button>
    );
  },
}
,


    {
      key: "school_id",
      label: "Distributor",
      className: "w-64 text-left",
      render: (user) => {
        const name =
          user?._school?.name ||               
          getSchoolName(user.school_id);     
        return (
          <span className="whitespace-normal leading-tight block">
            {name || "-"}
          </span>
        );
      },
    },

  ];

  const filteredUsers = users;

  const canExport = !loading && !searching && filteredUsers.length > 0;
  const exportDisabledReason = !filteredUsers.length
    ? "Tidak ada data untuk diekspor. Pastikan tabel berisi data."
    : loading || searching
      ? "Tunggu sampai data selesai dimuat."
      : "";

  const handleExport = async () => {
    if (!canExport) return;

    const toastId = toast.loading("Mengekspor data pengguna...");

    try {
      const store = getStore();
      const token = store.getItem("idToken");
      if (!token) throw new Error("Token tidak ada");

      const visibleIds = filteredUsers.map((u) => u.id).filter(Boolean);

      if (!visibleIds.length) {
        toast.update(toastId, {
          render: "Tidak ada data untuk diekspor.",
          type: "info",
          isLoading: false,
          autoClose: 2500,
        });
        return;
      }

      const params = new URLSearchParams();
      params.set("ids", visibleIds.join(","));

      const url = `${BASE_URL}/api/admin/users/export?${params.toString()}`;

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Export gagal (status ${res.status})`);
      }

      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().split("T")[0];

      a.href = downloadUrl;
      a.download = `pengguna_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);

      toast.update(toastId, {
        render: "Export berhasil. File sedang diunduh.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Terjadi kesalahan saat export.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const usersImportTemplate = useMemo(
    () => ({
      filename: "template_pengguna.csv",
      headers: ["Nama", "Email", "Role", "Distributor", "Kode Voucher"],
      sampleRow: [
        "Budi Santoso",
        "budi@example.com",
        "generalUser",
        "SMA 1 Jakarta",
        "ABC123,DEF456",
      ],
    }),
    []
  );

  const uploadUsersBulkFile = async (file) => {
    const toastId = toast.loading("Mengimpor pengguna...");

    try {
      const store = getStore();
      const token = store.getItem("idToken");

      if (!token) {
        toast.update(toastId, {
          render: "Sesi login sudah habis. Silakan login ulang.",
          type: "info",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      setImporting(true);
      setLoadError("");

      const res = await fetch(`${BASE_URL}/api/admin/users/bulk-import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const rawText = await res.text();
      let body = {};
      try {
        body = rawText ? JSON.parse(rawText) : {};
      } catch {
        // ignore
      }

      if (!res.ok) {
        toast.update(toastId, {
          render: body.message || "Gagal memproses file import.",
          type: "error",
          isLoading: false,
          autoClose: 4500,
        });
        return;
      }

     const rowErrors = (body?.rowErrors || []).map((x) => ({
      rowNumber: x.rowNumber,
      email: x.email,
      name: x.name,
      voucher: x.voucher, // ✅ if backend sends voucher in error entry
      reason: x.reason || x.message || "Error",
      code: x.code,
    }));

    const uiSummary = {
      totalRows: body?.summary?.totalRows ?? 0,
      createdCount: body?.summary?.createdCount ?? 0,
      skippedExisting: [],
      rowWarnings: [], // ✅ backend no longer uses warnings
      rowErrors,
    };

      setImportSummary(uiSummary);

      await fetchUsers({ reason: "load", targetPage: page });

     const total = body?.summary?.totalRows ?? uiSummary.totalRows ?? 0;
    const created = body?.summary?.createdCount ?? uiSummary.createdCount ?? 0;
    const errorCount = rowErrors.length;

    toast.update(toastId, {
      render:
        errorCount === 0
          ? `Import selesai: ${created}/${total} berhasil.`
          : `Import selesai: ${created}/${total} berhasil, ${errorCount} error.`,
      type: errorCount === 0 ? "success" : created > 0 ? "warning" : "error",
      isLoading: false,
      autoClose: 3500,
    });


      setShowImportModal(false);
    } catch (err) {
      console.error("[UsersManager] uploadUsersBulkFile error:", err);
      toast.update(toastId, {
        render: "Terjadi kesalahan saat mengimpor pengguna.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Pengguna</h2>
          <p className="text-gray-600 mt-1">Kelola data pengguna sistem</p>
          {loadError && <p className="mt-2 text-sm text-red-500">{loadError}</p>}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            disabled={importing}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              importing
                ? "bg-green-400 text-white cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            <Download className="w-4 h-4" />
            {importing ? "Mengimpor..." : "Import Excel/CSV"}
          </button>

          <button
            onClick={handleExport}
            disabled={!canExport}
            title={!canExport ? exportDisabledReason : "Export data sesuai tabel saat ini"}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              canExport
                ? "bg-gray-700 text-white hover:bg-gray-800"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            <Upload className="w-4 h-4" />
            Export Excel
          </button>

          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#4F7CFF] text-white rounded-lg hover:bg-[#3D6AEE] transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Tambah Pengguna
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4">
      <div className="flex-1">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Filter berdasarkan Role
  </label>

  <div className="relative">
    <select
      value={filterRole}
      onChange={(e) => setFilterRole(e.target.value)}
      className="w-full appearance-none px-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white
                 focus:outline-none focus:ring-2 focus:ring-[#4F7CFF] focus:border-transparent transition-all"
    >
      <option value="">Semua Role</option>
      <option value="admin">Admin</option>
      <option value="teacher">Guru</option>
      <option value="student">Siswa</option>
      <option value="principal">Kepala Sekolah</option>
      <option value="generalUser">Pengguna Umum</option>
      <option value="financeAdmin">Finance</option>
    </select>

    {/* Match Distributor chevron exactly */}
    <ChevronDown
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
    />
  </div>
</div>


        <div className="flex-1">
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Filter berdasarkan Distributor
      </label>

    <div className="relative">
<button
  type="button"
  onClick={() => setSchoolFocused((v) => !v)}
  className="w-full px-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-left
             focus:outline-none focus:ring-2 focus:ring-[#4F7CFF] focus:border-transparent transition-all"
>
  {filterSchool ? filterSchool : "Semua Distributor"}

  <ChevronDown
    className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 transition-transform ${
      schoolFocused ? "rotate-180" : ""
    }`}
  />
</button>

  {schoolFocused && (
    <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
      {/* ✅ max height + scroll */}
      <div className="max-h-64 overflow-y-auto">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setFilterSchool("");
            setSchoolFocused(false);
          }}
          className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
        >
          Semua Distributor
        </button>

        {allSchools.map((school) => (
          <button
            key={school.id}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setFilterSchool(school.name);
              setSchoolFocused(false);
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
          >
            {school.name}
          </button>
        ))}
      </div>
    </div>
  )}
</div>


        </div>
      </div>

      <DataTable
        ref={dataTableRef}
        data={users}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canDeleteRow={(row) => !isSelfUser(row)}
        onBulkDelete={handleBulkDelete}
        searchPlaceholder="Cari pengguna..."
        loading={loading}
        searching={searching}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        serverMode
        serverTotal={pagination.total}
        serverTotalPages={pagination.totalPages}
        onSearchChange={setServerSearch}
      />

      {showForm && (
        <UserForm
          user={editingUser}
          schools={schools}
          onClose={handleFormClose}
          onSaved={handleUserSaved}
        />
      )}

      {importSummary && (
        <BulkImportSummaryModal
          open={!!importSummary}
          summary={importSummary}
          onClose={() => setImportSummary(null)}
        />
      )}

      <BulkImportModal
        open={showImportModal}
        title="Import Pengguna"
        subtitle="Upload file Excel/CSV sesuai template pengguna."
        template={usersImportTemplate}
        uploading={importing}
        onClose={() => setShowImportModal(false)}
        onUpload={uploadUsersBulkFile}
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      />

      <VouchersModal
        open={showVouchersModal}
        onClose={() => {
          setShowVouchersModal(false);
          setVoucherModalUser(null);
        }}
        userName={voucherModalUser?.name}
        vouchers={voucherModalUser?.vouchers}
      />

      <DeleteConfirmModal
  open={deleteModal.open}
  title={deleteModal.title}
  message={deleteModal.message}
  tone={deleteModal.tone}
  confirmText={deleteModal.confirmText}
  cancelText={deleteModal.cancelText}
  onCancel={() => {
    deleteModal.resolve?.(false);
    setDeleteModal((p) => ({ ...p, open: false, resolve: null }));
  }}
  onConfirm={() => {
    deleteModal.resolve?.(true);
    setDeleteModal((p) => ({ ...p, open: false, resolve: null }));
  }}
/>

    </div>
  );
}
